import React, { useEffect, useState } from 'react';
import { useClickOutside } from 'refactored/ui/utils';
import { PremiumInputContainer } from './ui/PremiumInputContainer';
import { DropdownContainer } from './ui/DropdownContainer';
import { SearchContainer } from '../containers/searchContainer';
import { PremiumInput } from './customInput';
import { ClearButton } from '../buttons/ClearButton';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ItemsContainer } from '../containers/itemsContainer';
import { DropdownItem } from './ui/DropdownItem';
import BasicLoader from '../loaders/basic-loader';
import { LabelAndValue } from './ui/LabelAndValue';
import { PremiumLabelSmall } from './ui/PremiumLabelSmall';
import { RequiredIndicator } from './ui/RequiredIndicator';
import { Value } from './ui/Value';
import { PremiumLabelBig } from './ui/PremiumLabelBig';
import { Invisible } from './ui/Invisible';
import { UnitsForLabelAndValue } from './ui/UnitsForLabelAndValue';
import { PremiumUnit } from './ui/PremiumUnit';
import ArrowDown from '../icon/arrowDown';

const normalizeTextForSearch = (text) => {
  return (
    text
      //   ignore case
      .toUpperCase()
      // ignore weird letters
      .normalize('NFD')
      // ignore accents
      .replace(/\p{Diacritic}/gu, '')
      // custom ignore for zipcode match
      .replace(/-| /g, '')
  );
};

const search = (source, text) => {
  return normalizeTextForSearch(source).includes(normalizeTextForSearch(text));
};

const getFilteredOptions = (options, filterString) => {
  if (filterString.trim() === '') return options || [];
  return options?.filter((option) => {
    return search(option.search, filterString);
  });
};

const SelectWithSearchInput = ({
  onFieldChange,
  onSearchChange,
  options,
  isLoadingOptions,
  value,
  form,
  field,
  label,
  searchLabel,
  optionsEmptyMssg,
  isClereable = false,
  isDisabled,
  isRequired = false,
  inputRef,
  initialValue = '',
  ...props
}) => {
  const [hasFocus, setHasFocus] = useState(false);
  const [displayedValue, setDisplayedValue] = useState('');

  //updates the displayed value in case the field is changed externally
  useEffect(() => {
    const selectedValue = options?.filter((option) => option.value === value)[0]
      ?.display;
    //if coming from back the option with the value might not be there
    const valueToDisplay = value && !selectedValue ? value : selectedValue;
    setDisplayedValue(valueToDisplay);
  }, [options, value]);

  const clickRefFrom = React.useRef();
  useClickOutside(clickRefFrom, () => {
    setHasFocus(false);
  });
  const [optionFilterString, setOptionFilterString] = useState(initialValue);
  const filteredOptions = getFilteredOptions(options, optionFilterString);
  const onFilterChange = (value) => {
    onSearchChange && onSearchChange(value);
    setOptionFilterString(value);
  };
  const changeField = (field, value, selectedOption) => {
    if (onFieldChange !== undefined) {
      onFieldChange(value, form, selectedOption);
    } else {
      form.setFieldValue(field.name, value);
    }
  };
  return (
    <PremiumInputContainer
      width={props.width}
      hasFocus={hasFocus}
      ref={clickRefFrom}
      onClick={() => {
        !isDisabled && setHasFocus(true);
      }}
      isDisabled={isDisabled}
      {...props}
    >
      {hasFocus && (
        <DropdownContainer maxHeight={'250px'}>
          <SearchContainer>
            <PremiumInput
              autoFocus
              onChange={(e) => {
                onFilterChange(e.target.value);
              }}
              type="search"
              value={optionFilterString}
              label={searchLabel}
            />
            {!!isClereable && value !== '' && (
              <ClearButton
                onClick={() => {
                  onFilterChange('');
                  changeField(field, '');
                  setDisplayedValue('');
                }}
              >
                <CloseIcon />
              </ClearButton>
            )}
          </SearchContainer>
          <ItemsContainer>
            {isLoadingOptions && (
              <DropdownItem placeholder>
                <BasicLoader size={10} />
              </DropdownItem>
            )}
            {!isLoadingOptions && filteredOptions.length === 0 && (
              <DropdownItem placeholder>{optionsEmptyMssg}</DropdownItem>
            )}
            {filteredOptions.length > 0 && (
              <>
                {filteredOptions.map((item, i) => (
                  <DropdownItem
                    key={`option-${i}`}
                    isSelected={item.value === value}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setHasFocus(false);
                      onFilterChange('');
                      changeField(field, item.value, item);
                      setDisplayedValue(item.display);
                    }}
                  >
                    {item.label}
                  </DropdownItem>
                ))}
              </>
            )}
          </ItemsContainer>
        </DropdownContainer>
      )}
      {displayedValue ? (
        <LabelAndValue>
          <PremiumLabelSmall>
            {label}
            {isRequired && <RequiredIndicator />}
          </PremiumLabelSmall>
          <Value>
            <input
              ref={inputRef}
              disabled={isDisabled}
              value={displayedValue}
              onFocus={() => {
                setHasFocus(true);
              }}
              onBlur={() => {
                setHasFocus(false);
              }}
              {...props}
            />
          </Value>
        </LabelAndValue>
      ) : (
        <>
          <PremiumLabelBig>
            {label}
            {isRequired && <RequiredIndicator />}
          </PremiumLabelBig>
          <Invisible>
            <input
              ref={inputRef}
              disabled={isDisabled}
              value={displayedValue}
              onFocus={() => {
                setHasFocus(true);
              }}
              onBlur={() => {
                setHasFocus(false);
              }}
              {...props}
            />
          </Invisible>
          <UnitsForLabelAndValue noPadding={true}>
            <PremiumUnit>
              <ArrowDown />
            </PremiumUnit>
          </UnitsForLabelAndValue>
        </>
      )}
    </PremiumInputContainer>
  );
};
export default SelectWithSearchInput;
